import { OidcClient, OidcProvider } from "@axa-fr/react-oidc";
import { type ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import logoUrl from "@/assets/brand/logo-128.png";
import { oidcConfiguration, oidcIsConfigured } from "@/auth/oidc-configuration";
import { LanguageToggle } from "@/components/language-toggle";
import { LoadingScreen } from "@/components/loading-screen";
import { Button } from "@/components/ui/button";
import { detachWebPushLocally } from "@/features/notifications/web-push";
import { useI18n } from "@/locales/i18n-provider";
import { clearCurrentSessionCache } from "./session-cache";
import { getSessionExpirationState, notifySessionExpired, resetSessionExpiration } from "./session-expiration";
import { SessionExpiredNotice } from "./session-expired-notice";
import { SessionProvider } from "./session-provider";
import { useSessionExpiration } from "./use-session-expiration";

const sessionEndingEvents = new Set<string>([
  OidcClient.eventNames.logout_from_same_tab,
  OidcClient.eventNames.logout_from_another_tab,
  OidcClient.eventNames.refreshTokensAsync_error,
  OidcClient.eventNames.syncTokensAsync_error,
]);

function handleOidcEvent(_configurationName: string, eventName: string) {
  if (sessionEndingEvents.has(eventName)) {
    void detachWebPushLocally();
    clearCurrentSessionCache();
  }
  // 只有完成重新认证才恢复请求，后台 token 续签不能解除服务端会话失效。
  if (eventName === OidcClient.eventNames.loginCallbackAsync_end) {
    resetSessionExpiration();
  }
}

function OidcCallbackSuccess() {
  const { t } = useI18n();
  return <LoadingScreen detail={t("auth.callback.detail")} title={t("auth.callback.title")} variant="oidc" />;
}

function LoadingAuthentication() {
  const { t } = useI18n();
  return <LoadingScreen detail={t("auth.restore.detail")} title={t("auth.restore.title")} variant="page" />;
}

function Authenticating() {
  const { t } = useI18n();
  return <LoadingScreen detail={t("auth.redirect.detail")} title={t("auth.redirect.title")} variant="oidc" />;
}

function AuthenticationError() {
  const { t } = useI18n();
  return (
    <main className="grid min-h-dvh place-items-center bg-background px-6 py-12">
      <div className="fixed right-4 top-4">
        <LanguageToggle />
      </div>
      <section className="w-full max-w-115 rounded-xl border bg-card p-8 shadow-sm">
        <img alt="Tickbird" className="mb-6 size-14 rounded-lg" src={logoUrl} />
        <h1 className="text-2xl font-semibold text-foreground">{t("auth.error.title")}</h1>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">{t("auth.error.detail")}</p>
        <div className="mt-6">
          <Button onClick={() => window.location.reload()}>{t("auth.reload")}</Button>
        </div>
      </section>
    </main>
  );
}

function MissingConfiguration() {
  const { t } = useI18n();
  return (
    <main className="grid min-h-dvh place-items-center bg-background px-6 py-12">
      <div className="fixed right-4 top-4">
        <LanguageToggle />
      </div>
      <section className="w-full max-w-140 rounded-xl border bg-card p-8 shadow-sm">
        <img alt="Tickbird" className="mb-6 size-14 rounded-lg" src={logoUrl} />
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">{t("auth.firstStart")}</p>
        <h1 className="mt-2 text-2xl font-semibold text-foreground">{t("auth.missingConfig.title")}</h1>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          {t("auth.missingConfig.copy")} <code>.env.example</code> {t("auth.missingConfig.as")} <code>.env.local</code>
          {t("auth.missingConfig.finish")}
        </p>
      </section>
    </main>
  );
}

function SecuredApplication({ children }: { children: ReactNode }) {
  const location = useLocation();
  const callbackPath = `${location.pathname}${location.search}${location.hash}`;
  const expiration = useSessionExpiration();
  const oidc = OidcClient.get();

  useEffect(() => {
    // 首次访问仍自动登录；会话失效后的导航必须等待用户明确选择。
    if (oidc && !oidc.tokens && !oidc.isLoggingOut && getSessionExpirationState() === "active") {
      void oidc.loginAsync(callbackPath).catch(() => {
        // OidcProvider 通过 loginAsync_error 展示登录错误。
      });
    }
  }, [oidc, callbackPath]);

  if (!oidc?.tokens && expiration === "active") return null;

  // 登录完成后才取会话：准入与权限都来自 tickbird 自己，IAM 只负责认证。
  return <SessionProvider>{children}</SessionProvider>;
}

export function AuthenticationBoundary({ children }: { children: ReactNode }) {
  if (!oidcIsConfigured) {
    return <MissingConfiguration />;
  }

  return (
    <>
      <OidcProvider
        configuration={oidcConfiguration}
        callbackSuccessComponent={OidcCallbackSuccess}
        loadingComponent={LoadingAuthentication}
        authenticatingComponent={Authenticating}
        authenticatingErrorComponent={AuthenticationError}
        sessionLostComponent={AuthenticationError}
        onEvent={handleOidcEvent}
        onSessionLost={notifySessionExpired}
      >
        <SecuredApplication>{children}</SecuredApplication>
      </OidcProvider>
      <SessionExpiredNotice />
    </>
  );
}
